import { useEffect, useRef, useState } from 'react';

interface Props {
  value: string[];
  onChange: (v: string[]) => void;
  /** Prefix suggestion source (server autocomplete). */
  suggest: (q: string) => Promise<string[]>;
  /** Chips shown as quick-add presets (e.g. common instruments). */
  presets?: string[];
  placeholder?: string;
  max?: number;
  hint?: string;
}

/**
 * Free-text chips with autocomplete. Type and press Enter or comma to add;
 * ↓/↑ highlight a suggestion and Enter picks it; anything typed is accepted
 * so new genres / unusual instruments work. Used for genres, instruments and
 * the "I'm looking for" lists.
 */
export function TagPicker({ value, onChange, suggest, presets = [], placeholder, max = 30, hint }: Props) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<string[]>([]);
  const [hl, setHl] = useState(-1);
  const timer = useRef<number | null>(null);
  const latest = useRef(value);
  latest.current = value;

  useEffect(() => {
    if (timer.current) window.clearTimeout(timer.current);
    const s = q.trim();
    if (!s) { setHits([]); return; }
    timer.current = window.setTimeout(async () => {
      try {
        setHits((await suggest(s)).filter((g) => !latest.current.includes(g)));
        setHl(-1);
      } catch { setHits([]); }
    }, 250);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const norm = (raw: string) => raw.toLowerCase().replace(/\s+/g, ' ').trim().slice(0, 40);
  const add = (raw: string) => {
    const g = norm(raw);
    if (g && !value.includes(g) && value.length < max) onChange([...value, g]);
    setQ(''); setHits([]); setHl(-1);
  };
  const remove = (g: string) => onChange(value.filter((x) => x !== g));

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(hl >= 0 && hits[hl] ? hits[hl] : q); }
    else if (e.key === 'ArrowDown' && hits.length) { e.preventDefault(); setHl((i) => (i + 1) % hits.length); }
    else if (e.key === 'ArrowUp' && hits.length) { e.preventDefault(); setHl((i) => (i <= 0 ? hits.length - 1 : i - 1)); }
    else if (e.key === 'Escape') { setHits([]); setHl(-1); }
    else if (e.key === 'Backspace' && !q && value.length) onChange(value.slice(0, -1));
  };

  const unusedPresets = presets.filter((p) => !value.includes(p));

  return (
    <div className="stack">
      {value.length > 0 && (
        <div className="chips">
          {value.map((g) => <button type="button" key={g} className="chip on" onClick={() => remove(g)} title="Remove">{g} ✕</button>)}
        </div>
      )}
      <div className="dropdown">
        <input type="text" placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
          onBlur={() => q.trim() && add(q)} maxLength={40} autoCapitalize="none" />
        {hits.length > 0 && (
          <div className="menu">
            {hits.map((g, i) => (
              <button type="button" key={g} className={i === hl ? 'hl' : ''} onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setHl(i)} onClick={() => add(g)}>{g}</button>
            ))}
          </div>
        )}
      </div>
      {unusedPresets.length > 0 && (
        <div className="chips">
          {unusedPresets.map((p) => <button type="button" key={p} className="chip" onClick={() => add(p)}>{p}</button>)}
        </div>
      )}
      {hint && <div className="muted small">{hint}</div>}
    </div>
  );
}
